<template>
  <!-- Try it: the visitor picks how the reply should read and the draft changes in place. It never
       moves on its own. Compose, rewrite and translate are the app's AI writing tools (pricing
       matrix rows aiCompose and aiTranslateSummarize); the client message and the replies are
       sample text written for this page and stay untranslated, like other product mockups. -->
  <section class="srw">
    <div class="srw-shell srw-grid">
      <header class="srw-heading">
        <h2>{{ chapter.title }}</h2>
        <p class="srw-intro">{{ chapter.intro }}</p>
        <ul class="srw-points">
          <li v-for="point in chapter.points" :key="point">{{ point }}</li>
        </ul>
      </header>

      <div class="srw-demo">
        <div class="srw-card">
          <p class="srw-label">{{ chapter.incomingLabel }}</p>
          <div class="srw-incoming" translate="no">
            <span class="srw-avatar">MO</span>
            <p><b>Maya Ortiz</b> Hi! Can we move the shoot to next Thursday? And is the second invoice due before or after delivery?</p>
          </div>

          <div class="srw-tabs" role="group" :aria-label="chapter.tabsLabel">
            <button
              v-for="(tab, index) in chapter.tabs"
              :key="tab"
              type="button"
              class="srw-tab"
              :class="{ 'is-on': index === active }"
              :aria-pressed="index === active"
              @click="active = index"
            >
              <svg v-if="index > 0" viewBox="0 0 24 24" aria-hidden="true"><path :d="sparkle" /></svg>{{ tab }}
            </button>
          </div>

          <div class="srw-draft" aria-live="polite">
            <p class="srw-draft-meta">{{ chapter.draftLabel }}</p>
            <div class="srw-stack" translate="no">
              <p v-for="(reply, index) in replies" :key="index" class="srw-reply" :class="{ 'is-on': index === active }" :aria-hidden="index !== active">{{ reply }}</p>
            </div>
          </div>
          <p class="srw-note">{{ chapter.note }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  chapter: { type: Object, required: true },
});

const active = ref(0);
const sparkle = "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z";
// Draft, Shorter, Friendlier, Spanish (the order of chapter.tabs).
const replies = [
  "Hi Maya, Thursday works. I can move the shoot to October 30 at 10:00. The second invoice (INV-1051) is due on delivery, so nothing changes before the shoot. Let me know if that time suits you. Best, Sam",
  "Hi Maya, I can move the shoot to Thursday, Oct 30 at 10:00. INV-1051 is due on delivery. Sam",
  "Hi Maya, happy to move it! I can do Thursday, October 30 at 10:00. The second invoice, INV-1051, is due when we deliver the photos. Looking forward to the shoot! Sam",
  "Hola Maya: el jueves nos va bien. Puedo mover la sesión al 30 de octubre a las 10:00. La segunda factura (INV-1051) vence a la entrega, así que no cambia nada antes de la sesión. Avísame si ese horario te viene bien. Un saludo, Sam",
];
</script>

<style scoped>
.srw { background: #fff; padding: 6.5rem 0; }
.dark .srw { background: #020617; }
.srw-shell { width: min(100% - 3rem, var(--story-max, 84rem)); margin: 0 auto; }
.srw-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .srw-grid { grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr); gap: 4.5rem; } }
.srw-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .srw-heading h2 { color: #f8fafc; }
.srw-intro { margin-top: 1rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .srw-intro { color: #cbd5e1; }
.srw-points { display: grid; gap: 0.5rem; margin-top: 1.25rem; padding: 0; list-style: none; }
.srw-points li { position: relative; padding-left: 1rem; color: #334155; font-size: 0.95rem; line-height: 1.55; }
.dark .srw-points li { color: #cbd5e1; }
.srw-points li::before { content: ""; position: absolute; top: 0.6em; left: 0; width: 6px; height: 6px; border-radius: 2px; background: #1ba7ce; }

.srw-demo {
  border-radius: 14px;
  background:
    radial-gradient(circle, rgb(0 0 0 / 0.08) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    linear-gradient(135deg, rgb(124 155 224 / 0.12), rgb(159 139 230 / 0.06));
  padding: 2rem;
}
.dark .srw-demo { background: radial-gradient(circle, rgb(255 255 255 / 0.1) 0.8px, transparent 1.3px) 7px 7px / 14px 14px, linear-gradient(135deg, rgb(124 155 224 / 0.12), rgb(159 139 230 / 0.05)); }
.srw-card {
  max-width: 520px;
  margin: 0 auto;
  border: 1px solid rgb(0 0 0 / 0.1);
  border-radius: 10px;
  background: #fff;
  padding: 1rem 1.1rem 1.1rem;
  color: #171717;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13.5px;
  line-height: 1.5;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28);
}
.dark .srw-card { border-color: rgb(255 255 255 / 0.13); background: #1c1c1c; color: #ececec; box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7); }
.srw-label, .srw-draft-meta { color: #5f6368; font-size: 12px; font-weight: 500; }
.dark .srw-label, .dark .srw-draft-meta { color: #a8a8a8; }
.srw-incoming { display: flex; gap: 0.6rem; margin-top: 0.5rem; border-radius: 8px; background: #f4f6f8; padding: 0.7rem 0.8rem; }
.dark .srw-incoming { background: #262626; }
.srw-incoming b { display: block; font-weight: 600; }
.srw-avatar { display: grid; place-items: center; flex: none; width: 28px; height: 28px; border-radius: 7px; background: #e0e7ff; color: #3f58bf; font-size: 11px; font-weight: 700; }
.dark .srw-avatar { background: rgb(91 127 234 / 0.25); color: #93aef9; }

.srw-tabs { display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 1rem 0 0.75rem; }
.srw-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-height: 36px;
  border: 1px solid rgb(0 0 0 / 0.12);
  border-radius: 6px;
  background: #fff;
  padding: 0.35rem 0.7rem;
  color: #171717;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.srw-tab:hover { background: #f4f6f8; }
.srw-tab:active { transform: scale(0.97); }
.srw-tab:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }
.srw-tab svg { width: 13px; height: 13px; fill: currentColor; }
.srw-tab.is-on { border-color: #171717; background: #171717; color: #fff; }
.dark .srw-tab { border-color: rgb(255 255 255 / 0.15); background: #262626; color: #ececec; }
.dark .srw-tab:hover { background: #2f2f2f; }
.dark .srw-tab.is-on { border-color: #ececec; background: #ececec; color: #171717; }

.srw-draft { border: 1px solid rgb(0 0 0 / 0.1); border-radius: 8px; padding: 0.7rem 0.85rem; }
.dark .srw-draft { border-color: rgb(255 255 255 / 0.13); }
/* Every reply sits in the same cell, so the box is as tall as the longest one and never resizes. */
.srw-stack { display: grid; margin-top: 0.35rem; }
.srw-reply { grid-area: 1 / 1; margin: 0; opacity: 0; transform: translateY(6px); transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.33, 1, 0.68, 1); }
.srw-reply.is-on { opacity: 1; transform: none; }
.srw-note { margin-top: 0.75rem; color: #5f6368; font-size: 12px; }
.dark .srw-note { color: #a8a8a8; }
@media (max-width: 639px) {
  .srw { padding: 4.5rem 0; }
  .srw-shell { width: min(100% - 2rem, var(--story-max, 84rem)); }
  .srw-demo { padding: 0.9rem; }
}
@media (prefers-reduced-motion: reduce) {
  .srw-reply, .srw-tab { transition: none; }
}
</style>
